import React, { useState, useEffect, useMemo } from 'react';
import { TableSkeleton } from '../components/TableSkeleton';
import { useNavigate } from 'react-router-dom';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Textarea } from 'baseui/textarea';
import { Checkbox as BaseCheckbox, LABEL_PLACEMENT } from 'baseui/checkbox';

const checkboxOverrides = {
  Checkmark: {
    style: ({ $checked }: { $checked: boolean }) => ({
      backgroundColor: $checked ? 'var(--tenant-primary, var(--ink-900))' : undefined,
      borderColor: $checked ? 'var(--tenant-primary, var(--ink-900))' : undefined,
    }),
  },
};

const Checkbox: React.FC<React.ComponentProps<typeof BaseCheckbox>> = (props) => (
  <BaseCheckbox {...props} overrides={{ ...checkboxOverrides, ...(props.overrides || {}) }} />
);
import { LabelSmall, ParagraphSmall } from 'baseui/typography';
import { Building2, Trash2, Save, X, Copy, Info, Languages, KeyRound, User, Users, Package, Plus, Settings as SettingsIcon, ArrowLeft, ChevronDown, Smartphone, CreditCard, Maximize2, Minimize2, Check } from 'lucide-react';
import { toaster } from 'baseui/toast';
import { translate } from '../utils/translate';
import { useCreateBusiness, useUpdateBusiness, useDeleteBusiness, useBusinessCategories, useBusinessTypes, type Business } from '../hooks/useBusinessCrud';
import { S3ImageUpload } from '../components/S3ImageUpload';
import { S3Image } from '../components/S3Image';
import { SAVE_FAILED_TEXT, useHeaderSave } from './BusinessPage/headerSave';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { normalizeGateway, settingsForSave, type GatewayChoice } from './BusinessPage/gatewaySettings';
import { PaymentGatewayCard } from './BusinessPage/PaymentGatewayCard';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantBranding } from '../hooks/useTenantBranding';
import { BusinessLocationFields, BusinessLocationRead } from './BusinessPage/location/BusinessLocationFields';
import { EMPTY_LOCATION, locationFromBusiness, locationIssues, locationSummary, sameLocation, type LocationForm } from './BusinessPage/location/locationModel';
import { crudLocationClient, locationErrorText, saveLocation } from './BusinessPage/location/locationSave';
import { usePrimaryGroupId } from './BusinessPage/location/useLocationData';
import { useBusinessAccessInvitations, type BusinessAccess } from '../hooks/useBusinessAccessInvitations';
import { Drawer, SIZE as DRAWER_SIZE } from 'baseui/drawer';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton } from 'baseui/modal';
import { InvitationProvider } from './invitation/context/InvitationContext';
import { ConfirmationView } from './invitation/components/ConfirmationView';
import { GuestsView } from './invitation/Invitations';
import { useProducts, type Product } from '../hooks/useProducts';
import { ProductDetailDrawer } from '../components/ProductDetailDrawer';
import { useServices, type Service } from '../hooks/useServices';
import { BusinessPaymentsTable } from '../components/BusinessPaymentsTable';
import { BusinessAppPreview } from '../components/BusinessAppPreview';
import { getBusinessOpenState } from '../utils/businessHours';
import { describeCalendarEvent, nextCalendarEvent, parseCalendar } from './BusinessPage/businessCalendar';
import { BusinessTeamList } from '../components/BusinessTeamList';
import { PhoneInput } from '../components/ui/PhoneInput';
import { useAccessStatusLabelMap, getAccessTypeLabel } from '../hooks/useAccessTypeOptions';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
const DAYS: { key: DayKey; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

interface TimeRange {
  open: string;
  close: string;
}

interface DaySchedule {
  closed: boolean;
  // A day can have more than one block (e.g. 8:00–11:00 and 14:00–18:00).
  ranges: TimeRange[];
}

type WeekSchedule = Record<DayKey, DaySchedule>;

const DEFAULT_RANGE: TimeRange = { open: '09:00', close: '18:00' };

// Coerce any stored time token ("8:00 AM", "08:00", "8:00") into the 24h
// "HH:mm" shape that the <input type="time"> control requires. Legacy data may
// have been entered with AM/PM, so normalise it here instead of breaking the
// picker.
function toInputTime(raw: string): string {
  const m = (raw || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return raw || '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const mer = (m[3] || '').toUpperCase();
  if (mer === 'PM' && h < 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
}

function emptySchedule(): WeekSchedule {
  const out = {} as WeekSchedule;
  DAYS.forEach(({ key }) => {
    out[key] = { closed: true, ranges: [{ ...DEFAULT_RANGE }] };
  });
  return out;
}

function parseDay(times: string[] | undefined): DaySchedule {
  if (!times || times.length === 0) {
    return { closed: true, ranges: [{ ...DEFAULT_RANGE }] };
  }
  const ranges = times
    .map((t) => {
      const [open, close] = (t || '').split('-').map((s) => s.trim());
      return { open: toInputTime(open) || DEFAULT_RANGE.open, close: toInputTime(close) || DEFAULT_RANGE.close };
    })
    .filter((r) => r.open || r.close);
  return { closed: false, ranges: ranges.length ? ranges : [{ ...DEFAULT_RANGE }] };
}

function serializeDay(day: DaySchedule): string[] {
  if (day.closed) return [];
  return day.ranges
    .filter((r) => r.open && r.close)
    .map((r) => `${r.open}-${r.close}`);
}

/** Collapsible section used to group business info cleanly (prototype style). */
const AccSection: React.FC<{
  title: string;
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, summary, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`bz-acc ${open ? 'is-open' : ''}`}>
      <button type="button" className="bz-acc-head" onClick={() => setOpen((o) => !o)}>
        <span className="bz-acc-title">{title}</span>
        {summary != null && summary !== '' && <span className="bz-acc-summary">{summary}</span>}
        <ChevronDown size={16} className="bz-acc-chevron" />
      </button>
      {open && <div className="bz-acc-body">{children}</div>}
    </section>
  );
};

interface BusinessDetailPanelProps {
  business: Business | null;
  mode: 'create' | 'edit';
  isAdmin: boolean;
  onClose: () => void;
  onSaved?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /**
   * SAM-198 — 'page' renders one Settings section of the full-page business view
   * (/business/:id) instead of the old side panel: no header, tabs or footer, a
   * sticky Save/Cancel bar only while there are changes, and the app preview beside
   * Profile and Schedule. 'panel' (default) is the old split-view panel.
   */
  variant?: 'panel' | 'page';
  section?: BusinessSettingsSection;
}

export type BusinessSettingsSection = 'profile' | 'schedule' | 'advanced';

export const BusinessDetailPanel: React.FC<BusinessDetailPanelProps> = ({
  business,
  mode,
  isAdmin,
  onClose,
  onSaved,
  isFullscreen = false,
  onToggleFullscreen,
  variant = 'panel',
  section = 'profile',
}) => {
  const isPage = variant === 'page';
  // S-78: the gateway credentials are Xhinola's alone; isAdmin also covers the tenant admin.
  const { isXhinolaAdmin, isTenantSettings } = useUserPermissions();
  const queryClient = useQueryClient();
  const { tenantName } = useTenantBranding();
  const createBusiness = useCreateBusiness();
  const updateBusiness = useUpdateBusiness();
  const deleteBusiness = useDeleteBusiness();

  const { data: categories = [] } = useBusinessCategories();
  const { data: businessTypes = [] } = useBusinessTypes();
  const [subcatError, setSubcatError] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    business_category_id: null as number | null,
    business_type_id: null as number | null,
    description_es: '',
    description_en: '',
    phone_number: '',
    email: '',
    website_url: '',
    instagram_url: '',
    x_url: '',
    facebook_url: '',
    logo_url: '',
    background_img_url: '',
    is_active: true,
    show_in_app: true,
    show_gatekeeper: true,
    schedule: emptySchedule(),
    carnet_merchant_id: '',
    carnet_terminal_id: '',
    payment_gateway: '' as GatewayChoice | '',
  });
  const [editingGateway, setEditingGateway] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Page variant: the form as loaded (or last saved), so the Save/Cancel bar only
  // shows while something changed.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  type TabKey = 'general' | 'profile' | 'catalog' | 'payments' | 'team';
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const [showCarnetConfig, setShowCarnetConfig] = useState(false);
  const [descLang, setDescLang] = useState<'es' | 'en'>('es');
  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslateDescription = async () => {
    const sourceLang = descLang === 'es' ? 'en' : 'es';
    const sourceText = sourceLang === 'es' ? formData.description_es : formData.description_en;
    if (!sourceText.trim() || isTranslating) return;
    setIsTranslating(true);
    try {
      const translated = await translate(sourceText, sourceLang, descLang);
      if (translated && translated.trim()) {
        setFormData((p) => ({
          ...p,
          [descLang === 'es' ? 'description_es' : 'description_en']: translated,
        }));
      } else {
        toaster.warning('Translation came back empty', { autoHideDuration: 2500 });
      }
    } catch {
      toaster.negative('Translation failed', { autoHideDuration: 2500 });
    } finally {
      setIsTranslating(false);
    }
  };

  useEffect(() => {
    let next: typeof formData;
    if (mode === 'edit' && business) {
      const settings = (business as any).settings || {};
      next = {
        name: business.name || '',
        business_category_id: (business as any).business_category_id ?? null,
        business_type_id: (business as any).business_type_id ?? null,
        description_es: business.description_es || '',
        description_en: business.description_en || '',
        phone_number: business.phone_number || '',
        email: business.email || '',
        website_url: business.website_url || '',
        instagram_url: business.instagram_url || '',
        x_url: business.x_url || '',
        facebook_url: business.facebook_url || '',
        logo_url: business.logo_url || '',
        background_img_url: business.background_img_url || '',
        is_active: business.is_active ?? true,
        show_in_app: business.show_in_app ?? true,
        show_gatekeeper: business.show_gatekeeper ?? true,
        schedule: {
          mon: parseDay(business.schedule_mon),
          tue: parseDay(business.schedule_tue),
          wed: parseDay(business.schedule_wed),
          thu: parseDay(business.schedule_thu),
          fri: parseDay(business.schedule_fri),
          sat: parseDay(business.schedule_sat),
          sun: parseDay(business.schedule_sun),
        },
        carnet_merchant_id: settings.merchant_id || '',
        carnet_terminal_id: settings.terminal_id || '',
        payment_gateway: normalizeGateway(settings.payment_gateway),
      };
    } else {
      next = {
        name: '',
        business_category_id: null,
        business_type_id: null,
        description_es: '',
        description_en: '',
        phone_number: '',
        email: '',
        website_url: '',
        instagram_url: '',
        x_url: '',
        facebook_url: '',
        logo_url: '',
        background_img_url: '',
        is_active: true,
        show_in_app: true,
        show_gatekeeper: true,
        schedule: emptySchedule(),
        carnet_merchant_id: '',
        carnet_terminal_id: '',
        payment_gateway: '',
      };
    }
    setFormData(next);
    setSavedSnapshot(JSON.stringify(next));
    setShowDeleteConfirm(false);
    setSubcatError(false);
  }, [business, mode]);

  const isDirty = savedSnapshot !== null && savedSnapshot !== JSON.stringify(formData);
  // SAM-1237: the location is saved on its own (crud checks it against the estate group), through the same header bar.
  const canEditLocation = isPage && mode === 'edit' && !!business && (isAdmin || isTenantSettings);
  const { data: primaryGroupId, isFetched: primaryGroupFetched } = usePrimaryGroupId(isPage && mode === 'edit' ? business?.id : null);
  const storedLocation = useMemo(
    () => locationFromBusiness(business, primaryGroupId ?? null),
    [business, primaryGroupId],
  );
  const [locForm, setLocForm] = useState<LocationForm>(EMPTY_LOCATION);
  const [locEditing, setLocEditing] = useState(false);
  const [locSaving, setLocSaving] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);
  const [locTried, setLocTried] = useState(false);
  useEffect(() => { if (!locEditing) setLocForm(storedLocation); }, [storedLocation, locEditing]);
  const locDirty = locEditing && !sameLocation(locForm, storedLocation);
  const locBlocked = locDirty && locationIssues(locForm).length > 0;

  const handleReset = () => {
    setEditingGateway(false);
    if (savedSnapshot) setFormData(JSON.parse(savedSnapshot));
    setSubcatError(false);
    setLocForm(storedLocation);
    setLocEditing(false);
    setLocError(null);
    setLocTried(false);
  };

  const handleSave = () => {
    // A category without a subcategory is incomplete classification (SAM-163).
    if (formData.business_category_id != null && formData.business_type_id == null) {
      setSubcatError(true);
      toaster.warning('Pick a subcategory', { autoHideDuration: 2500 });
      return;
    }
    const data: Partial<Business> = {
      name: formData.name,
      business_category_id: formData.business_category_id,
      business_type_id: formData.business_type_id,
      description_es: formData.description_es || undefined,
      description_en: formData.description_en || undefined,
      phone_number: formData.phone_number || undefined,
      email: formData.email || undefined,
      website_url: formData.website_url || undefined,
      instagram_url: formData.instagram_url || undefined,
      x_url: formData.x_url || undefined,
      facebook_url: formData.facebook_url || undefined,
      logo_url: formData.logo_url || undefined,
      background_img_url: formData.background_img_url || undefined,
      is_active: formData.is_active,
      show_in_app: formData.show_in_app,
      show_gatekeeper: formData.show_gatekeeper,
      schedule_mon: serializeDay(formData.schedule.mon),
      schedule_tue: serializeDay(formData.schedule.tue),
      schedule_wed: serializeDay(formData.schedule.wed),
      schedule_thu: serializeDay(formData.schedule.thu),
      schedule_fri: serializeDay(formData.schedule.fri),
      schedule_sat: serializeDay(formData.schedule.sat),
      schedule_sun: serializeDay(formData.schedule.sun),
    };

    // Merge Cardnet credentials into business.settings without dropping any
    // other keys the backend may have stored there. Empty fields are cleared
    // so removing a credential actually removes it server-side. Only Xhinola
    // sends settings (S-78); for anyone else the request leaves them alone.
    const mergedSettings = settingsForSave(
      (business as any)?.settings, formData.carnet_merchant_id, formData.carnet_terminal_id, isXhinolaAdmin, formData.payment_gateway,
    );
    if (mergedSettings) (data as any).settings = mergedSettings;

    const done = () => {
      onSaved?.();
      setEditingGateway(false);
      if (isPage && mode === 'edit') setSavedSnapshot(JSON.stringify(formData));
      else onClose();
    };
    if (mode === 'create') {
      createBusiness.mutate(data, { onSuccess: done });
    } else if (business) {
      updateBusiness.mutate({ id: business.id, data }, { onSuccess: done });
    }
  };

  const handleCopyDayToAll = (sourceKey: DayKey) => {
    setFormData((p) => {
      const source = p.schedule[sourceKey];
      const next = { ...p.schedule };
      DAYS.forEach(({ key }) => {
        // Deep-copy ranges so each day owns its own array/objects.
        if (key !== sourceKey) next[key] = { closed: source.closed, ranges: source.ranges.map((r) => ({ ...r })) };
      });
      return { ...p, schedule: next };
    });
    const label = DAYS.find((d) => d.key === sourceKey)?.label;
    toaster.positive(`${label}'s schedule applied to all days`, { autoHideDuration: 2000 });
  };

  const setDayClosed = (key: DayKey, closed: boolean) => {
    setFormData((p) => ({
      ...p,
      schedule: { ...p.schedule, [key]: { ...p.schedule[key], closed } },
    }));
  };

  const setRange = (key: DayKey, idx: number, field: 'open' | 'close', value: string) => {
    setFormData((p) => {
      const ranges = p.schedule[key].ranges.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
      return { ...p, schedule: { ...p.schedule, [key]: { ...p.schedule[key], ranges } } };
    });
  };

  const addRange = (key: DayKey) => {
    setFormData((p) => {
      const ranges = [...p.schedule[key].ranges, { ...DEFAULT_RANGE }];
      return { ...p, schedule: { ...p.schedule, [key]: { ...p.schedule[key], ranges } } };
    });
  };

  const removeRange = (key: DayKey, idx: number) => {
    setFormData((p) => {
      const filtered = p.schedule[key].ranges.filter((_, i) => i !== idx);
      // Never leave a day with zero ranges; fall back to a default block.
      const ranges = filtered.length ? filtered : [{ ...DEFAULT_RANGE }];
      return { ...p, schedule: { ...p.schedule, [key]: { ...p.schedule[key], ranges } } };
    });
  };

  const handleDelete = () => {
    if (!business) return;
    deleteBusiness.mutate(business.id, { onSuccess: () => { onSaved?.(); onClose(); } });
  };

  const handleSaveAll = async () => {
    if (locDirty && business) {
      setLocTried(true);
      if (locBlocked) return;
      setLocError(null);
      setLocSaving(true);
      try {
        await saveLocation(crudLocationClient, business.id, storedLocation, locForm);
        await queryClient.invalidateQueries({ queryKey: ['business-primary-group'] });
        await queryClient.invalidateQueries({ queryKey: ['businesses-crud'] });
        setLocEditing(false);
        setLocTried(false);
        if (!isDirty) onSaved?.();
      } catch (error) {
        setLocError(locationErrorText(error));
        setLocSaving(false);
        return;
      }
      setLocSaving(false);
    }
    if (isDirty) handleSave();
  };

  const isSaving = createBusiness.isPending || updateBusiness.isPending || locSaving;
  const isDeleting = deleteBusiness.isPending;
  const isFormValid = formData.name.trim().length > 0;

  // SAM-1167: on the business page the save state shows in the page header, not in a banner here.
  const failedSave = updateBusiness.isError ? updateBusiness.error : createBusiness.isError ? createBusiness.error : null;
  useHeaderSave(
    {
      visible: isPage && (isDirty || locDirty || mode === 'create'),
      mode,
      saving: isSaving,
      error: locError ?? (failedSave ? SAVE_FAILED_TEXT : null),
      saveDisabled: !isFormValid,
      createLabel: 'Create business',
    },
    {
      onSave: handleSaveAll,
      onCancel: () => { updateBusiness.reset(); handleReset(); },
    },
  );
  const title = mode === 'create' ? 'New Business' : business?.name || 'Business';

  // Which blocks render: by tab in the panel, by Settings section on the page.
  const showIdentity = isPage ? section === 'profile' : activeTab === 'general';
  const showVisibility = isPage ? section === 'advanced' : activeTab === 'general';
  const showBranding = isPage ? section === 'profile' : activeTab === 'profile';
  const showSchedule = isPage ? section === 'schedule' : activeTab === 'profile';
  const showCarnetInline = isPage && section === 'advanced' && isXhinolaAdmin;
  const showPagePreview = isPage && (section === 'profile' || section === 'schedule');

  return (
    <div className={`bk-detail-pane-inner ${isPage ? 'bz-page-settings' : ''}`}>
      {!isPage && (<>
      <header className="bk-detail-header">
        <div className="bk-detail-header-left">
          <button className="bk-icon-btn bk-back-btn" onClick={onClose} aria-label="Back to list">
            <ArrowLeft size={16} />
          </button>
          <div className="bz-avatar">
            {mode === 'edit' && business?.logo_url ? (
              <S3Image
                imageKey={business.logo_url}
                alt="Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Building2 size={20} color="var(--white)" />
            )}
          </div>
          <div>
            <div className="bk-detail-title">{title}</div>
            <div className="bk-detail-sub">
              {mode === 'create' ? (
                'Create a new business'
              ) : (
                // Short code per the design; click copies the full UUID.
                <span
                  role="button"
                  title={`${business?.id} — click to copy`}
                  style={{ cursor: 'copy' }}
                  onClick={() => {
                    if (!business?.id) return;
                    navigator.clipboard?.writeText(business.id);
                    toaster.info('Business ID copied', { autoHideDuration: 1500 });
                  }}
                >
                  {`ID: ${(business?.id || '').slice(0, 4)}-${(business?.id || '').slice(-4)}`}
                </span>
              )}
            </div>
          </div>
        </div>
        <button className="bk-icon-btn" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </header>

      <div className="bz-tabs">
        <button type="button" className={`bz-tab ${activeTab === 'general' ? 'is-active' : ''}`} onClick={() => setActiveTab('general')}>
          <Info size={14} /> General
        </button>
        <button type="button" className={`bz-tab ${activeTab === 'profile' ? 'is-active' : ''}`} onClick={() => setActiveTab('profile')}>
          <Smartphone size={14} /> App Profile
        </button>
        {mode === 'edit' && business && (
          <button type="button" className={`bz-tab ${activeTab === 'catalog' ? 'is-active' : ''}`} onClick={() => setActiveTab('catalog')}>
            <Package size={14} /> Products &amp; Services
          </button>
        )}
        <button type="button" className={`bz-tab ${activeTab === 'payments' ? 'is-active' : ''}`} onClick={() => setActiveTab('payments')}>
          <CreditCard size={14} /> Payments
        </button>
        {mode === 'edit' && business && (
          <button type="button" className={`bz-tab ${activeTab === 'team' ? 'is-active' : ''}`} onClick={() => setActiveTab('team')}>
            <Users size={14} /> Team
          </button>
        )}
        {/* Access tab temporarily disabled — keep for later:
        {mode === 'edit' && business && (
          <button type="button" className={`bz-tab ${activeTab === 'access' ? 'is-active' : ''}`} onClick={() => setActiveTab('access')}>
            <KeyRound size={14} /> Access
          </button>
        )} */}
      </div>
      </>)}

      <div className={`bk-detail-body ${(isFullscreen && activeTab === 'profile') || showPagePreview ? 'has-preview' : ''}`}>
      <div className="bk-detail-scroll">
        {(showIdentity || showVisibility) && (
          <div className="bz-tab-panel">
            {showIdentity && (<>
            <AccSection title="Identity" summary={formData.name || undefined}>
              <div className="bz-grid-2">
                <FormControl label="Name">
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Business name"
                  />
                </FormControl>
                <FormControl label="Email">
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="Email address"
                    type="email"
                  />
                </FormControl>
                <FormControl label="Phone Number">
                  <PhoneInput
                    value={formData.phone_number}
                    onChange={(phone) => setFormData((p) => ({ ...p, phone_number: phone }))}
                  />
                </FormControl>
              </div>
            </AccSection>

            <AccSection
              title="Classification"
              summary={
                [
                  categories.find((c) => c.id === formData.business_category_id)?.name,
                  businessTypes.find((t) => t.id === formData.business_type_id)?.name,
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
            >
              <div className="bz-grid-2">
                <FormControl label="Category">
                  <select
                    className="bz-native-select"
                    value={formData.business_category_id ?? ''}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      // Changing category invalidates the previous subcategory.
                      setFormData((p) => ({ ...p, business_category_id: v, business_type_id: null }));
                      setSubcatError(false);
                    }}
                  >
                    <option value="">Select…</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </FormControl>
                <FormControl
                  label={formData.business_category_id != null ? 'Subcategory *' : 'Subcategory'}
                  error={subcatError ? 'Subcategory is required' : null}
                >
                  <select
                    className={`bz-native-select ${subcatError ? 'is-error' : ''}`}
                    value={formData.business_type_id ?? ''}
                    disabled={formData.business_category_id == null}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      setFormData((p) => ({ ...p, business_type_id: v }));
                      if (v != null) setSubcatError(false);
                    }}
                  >
                    <option value="">
                      {formData.business_category_id == null ? '—' : 'Select a subcategory…'}
                    </option>
                    {businessTypes
                      .filter((t) => t.business_category_id === formData.business_category_id)
                      .map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                  </select>
                </FormControl>
              </div>
            </AccSection>

            {isPage && mode === 'edit' && business && primaryGroupFetched && (
              <AccSection
                title="Location"
                summary={locationSummary(storedLocation, { tenant: tenantName }) || (storedLocation.type === 'internal' ? (tenantName || 'Cap Cana') : storedLocation.type === 'virtual' ? 'Virtual' : undefined)}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p className="m-0 text-[13px] text-[var(--ink-500)]">Shown in the business profile in the app.</p>
                  {canEditLocation && !locEditing && (
                    <button
                      type="button"
                      onClick={() => setLocEditing(true)}
                      className="h-[34px] px-4 rounded-[10px] border border-solid border-[var(--line)] bg-white text-[14px] font-semibold text-[var(--ink-700)] cursor-pointer"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {locEditing ? (
                  <BusinessLocationFields value={locForm} onChange={setLocForm} tenantName={tenantName} showIssues={locTried} />
                ) : (
                  <BusinessLocationRead value={storedLocation} tenantName={tenantName} />
                )}
              </AccSection>
            )}

            </>)}
            {showVisibility && (<>
            <AccSection
              title="Visibility"
              summary={[
                formData.is_active ? 'Active' : 'Inactive',
                formData.show_in_app ? 'In app' : null,
                formData.show_gatekeeper ? 'Front desk' : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            >
              <div className="bz-super-note">
                <Info size={15} />
                <span>These switches are for super-admins — some businesses live only in the app, others only at the front desk.</span>
              </div>

              <div
                className="bz-opt-row"
                role="button"
                onClick={() => setFormData((p) => ({ ...p, is_active: !p.is_active }))}
              >
                <div className="bz-opt">
                  <div className="bz-opt-title">Active</div>
                  <div className="bz-opt-hint">Master switch. Inactive businesses are hidden everywhere and can’t be booked.</div>
                </div>
                <span className={`bz-chk ${formData.is_active ? 'on' : ''}`}><Check size={13} /></span>
              </div>

              <div
                className="bz-opt-row"
                role="button"
                onClick={() => setFormData((p) => ({ ...p, show_in_app: !p.show_in_app }))}
              >
                <div className="bz-opt">
                  <div className="bz-opt-title">Show in app</div>
                  <div className="bz-opt-hint">Visible to guests in the mobile app.</div>
                </div>
                <span className={`bz-chk ${formData.show_in_app ? 'on' : ''}`}><Check size={13} /></span>
              </div>

              <div
                className="bz-opt-row"
                role="button"
                onClick={() => setFormData((p) => ({ ...p, show_gatekeeper: !p.show_gatekeeper }))}
              >
                <div className="bz-opt">
                  <div className="bz-opt-title">Show at front desk</div>
                  <div className="bz-opt-hint">Visible to gatekeepers / front-desk access screens.</div>
                </div>
                <span className={`bz-chk ${formData.show_gatekeeper ? 'on' : ''}`}><Check size={13} /></span>
              </div>
            </AccSection>

            {showCarnetInline && (
              <PaymentGatewayCard
                businessId={business?.id || ''}
                gateway={formData.payment_gateway}
                merchantId={formData.carnet_merchant_id}
                terminalId={formData.carnet_terminal_id}
                editing={editingGateway}
                onEdit={() => setEditingGateway(true)}
                onCancelEdit={handleReset}
                onChange={(patch) => setFormData((p) => ({
                  ...p,
                  ...(patch.gateway !== undefined ? { payment_gateway: patch.gateway } : {}),
                  ...(patch.merchantId !== undefined ? { carnet_merchant_id: patch.merchantId } : {}),
                  ...(patch.terminalId !== undefined ? { carnet_terminal_id: patch.terminalId } : {}),
                }))}
              />
            )}

            {mode === 'edit' && business && isAdmin && (
              <AccSection title="Danger Zone" defaultOpen={false}>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    className="bz-danger-btn"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting}
                  >
                    <Trash2 size={14} />
                    Delete Business
                  </button>
                ) : (
                  <div className="bz-danger-confirm">
                    <p style={{ margin: '0 0 12px', color: 'var(--ink-700)', fontSize: '13px', fontWeight: 500 }}>
                      This action is permanent and cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="bz-secondary-btn"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="bz-danger-btn"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        style={{ flex: 1 }}
                      >
                        <Trash2 size={14} />
                        {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                      </button>
                    </div>
                  </div>
                )}
              </AccSection>
            )}
            </>)}
          </div>
        )}

        {showBranding && (
          <div className="bz-tab-panel">
            {onToggleFullscreen && (
              <button type="button" className="bz-fs-btn" onClick={onToggleFullscreen}>
                {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                {isFullscreen ? 'Exit full screen' : 'Go full screen to preview this profile in the app'}
              </button>
            )}
            <AccSection title="Branding">
              <div className="bz-branding-grid">
                <div className="bz-brand-field">
                  <div className="bz-section-title">Logo</div>
                  <S3ImageUpload
                    variant="slot"
                    value={formData.logo_url}
                    onChange={(key) => setFormData((p) => ({ ...p, logo_url: key }))}
                    placeholder="Logo"
                    slotWidth="150px"
                    slotHeight="150px"
                    objectFit="contain"
                  />
                </div>
                <div className="bz-brand-field">
                  <div className="bz-section-title">Background</div>
                  <S3ImageUpload
                    variant="slot"
                    value={formData.background_img_url}
                    onChange={(key) => setFormData((p) => ({ ...p, background_img_url: key }))}
                    placeholder="Background"
                    slotWidth="100%"
                    slotHeight="150px"
                    objectFit="cover"
                  />
                </div>
              </div>

              {/* SAM-164: make the live App Profile preview discoverable right
                  from the Branding section. Previously the phone mockup only
                  appeared after clicking the small "Go full screen" button at the
                  top of the tab, so QA reported it as missing. This persistent
                  affordance sits next to the LOGO/BACKGROUND uploaders and opens
                  the same live preview rail. */}
              {onToggleFullscreen && !isFullscreen && (
                <button
                  type="button"
                  className="bz-brand-preview-cta"
                  onClick={onToggleFullscreen}
                >
                  <span className="bz-brand-preview-cta-icon">
                    <Smartphone size={18} />
                  </span>
                  <span className="bz-brand-preview-cta-text">
                    <span className="bz-brand-preview-cta-title">Preview in app</span>
                    <span className="bz-brand-preview-cta-sub">
                      See how the logo &amp; banner render on the phone
                    </span>
                  </span>
                  <Maximize2 size={15} />
                </button>
              )}
            </AccSection>

            <AccSection title="App Description" summary="Shown to guests in the app">
              <div className="bz-lang-tabs" role="tablist" aria-label="Description language">
                <button
                  type="button"
                  className={`bz-lang-tab ${descLang === 'es' ? 'is-active' : ''}`}
                  onClick={() => setDescLang('es')}
                >
                  Español
                  {!formData.description_es.trim() && (
                    <span className="bz-lang-dot" title="Spanish description is empty" />
                  )}
                </button>
                <button
                  type="button"
                  className={`bz-lang-tab ${descLang === 'en' ? 'is-active' : ''}`}
                  onClick={() => setDescLang('en')}
                >
                  English
                  {!formData.description_en.trim() && (
                    <span className="bz-lang-dot" title="English description is empty" />
                  )}
                </button>
              </div>
              {descLang === 'es' ? (
                <FormControl key="es">
                  <Textarea
                    value={formData.description_es}
                    onChange={(e) => setFormData((p) => ({ ...p, description_es: (e.target as HTMLTextAreaElement).value }))}
                    placeholder="Descripción en español"
                    size="compact"
                    overrides={{ Input: { style: { minHeight: '120px', resize: 'vertical' } } }}
                  />
                </FormControl>
              ) : (
                <FormControl key="en">
                  <Textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData((p) => ({ ...p, description_en: (e.target as HTMLTextAreaElement).value }))}
                    placeholder="Description in English"
                    size="compact"
                    overrides={{ Input: { style: { minHeight: '120px', resize: 'vertical' } } }}
                  />
                </FormControl>
              )}
              {(() => {
                const currentEmpty = descLang === 'es'
                  ? !formData.description_es.trim()
                  : !formData.description_en.trim();
                const otherHasText = descLang === 'es'
                  ? formData.description_en.trim().length > 0
                  : formData.description_es.trim().length > 0;
                if (!currentEmpty || !otherHasText) return null;
                const otherLabel = descLang === 'es' ? 'English' : 'Spanish';
                return (
                  <button
                    type="button"
                    className="bz-translate-btn"
                    onClick={handleTranslateDescription}
                    disabled={isTranslating}
                  >
                    <Languages size={14} />
                    {isTranslating ? 'Translating…' : `Translate from ${otherLabel}`}
                  </button>
                );
              })()}
            </AccSection>

            <AccSection title="Web & Social">
              <div className="bz-grid-2">
                <FormControl label="Website URL">
                  <Input
                    value={formData.website_url}
                    onChange={(e) => setFormData((p) => ({ ...p, website_url: e.target.value }))}
                    placeholder="https://example.com"
                  />
                </FormControl>
                <FormControl label="Instagram URL">
                  <Input
                    value={formData.instagram_url}
                    onChange={(e) => setFormData((p) => ({ ...p, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/..."
                  />
                </FormControl>
                <FormControl label="X (Twitter) URL">
                  <Input
                    value={formData.x_url}
                    onChange={(e) => setFormData((p) => ({ ...p, x_url: e.target.value }))}
                    placeholder="https://x.com/..."
                  />
                </FormControl>
                <FormControl label="Facebook URL">
                  <Input
                    value={formData.facebook_url}
                    onChange={(e) => setFormData((p) => ({ ...p, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/..."
                  />
                </FormControl>
              </div>
            </AccSection>
          </div>
        )}

        {showSchedule && (
          <div className="bz-tab-panel">
        <AccSection title="Schedule">
          <p style={{ margin: '0 0 12px', color: 'var(--ink-500)', fontSize: 13 }}>
            Add multiple time blocks for a day with split hours (e.g. 8:00–11:00
            and 14:00–18:00).
          </p>
          {DAYS.map(({ key, label }) => {
            const day = formData.schedule[key];
            return (
              <div
                key={key}
                className="bz-schedule-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 100px 1fr 32px',
                  alignItems: 'start',
                  gap: '10px',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--line-subtle)',
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 500, paddingTop: 8 }}>{label}</div>
                <div style={{ paddingTop: 6 }}>
                  <Checkbox
                    checked={day.closed}
                    onChange={(e) => setDayClosed(key, e.target.checked)}
                    labelPlacement="right"
                  >
                    Closed
                  </Checkbox>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {day.closed ? (
                    <p style={{ margin: '6px 0 0', color: 'var(--ink-400)', fontSize: 13 }}>
                      Closed all day
                    </p>
                  ) : (
                    <>
                      {day.ranges.map((r, idx) => (
                        <div
                          key={idx}
                          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: '8px', alignItems: 'center' }}
                        >
                          <Input
                            type="time"
                            value={r.open}
                            onChange={(e) => setRange(key, idx, 'open', e.target.value)}
                          />
                          <Input
                            type="time"
                            value={r.close}
                            onChange={(e) => setRange(key, idx, 'close', e.target.value)}
                          />
                          <button
                            type="button"
                            className="bz-icon-btn"
                            onClick={() => removeRange(key, idx)}
                            title="Remove this block"
                            aria-label="Remove this block"
                            disabled={day.ranges.length <= 1}
                            style={{ opacity: day.ranges.length <= 1 ? 0.35 : 1 }}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="bz-secondary-btn"
                        onClick={() => addRange(key)}
                        style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 12 }}
                      >
                        <Plus size={13} />
                        Add block
                      </button>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="bz-copy-day-btn"
                  onClick={() => handleCopyDayToAll(key)}
                  title={`Apply ${label}'s hours to all days`}
                  aria-label={`Apply ${label}'s hours to all days`}
                  style={{ marginTop: 4 }}
                >
                  <Copy size={13} />
                </button>
              </div>
            );
          })}
        </AccSection>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="bz-tab-panel">
            <div className="bz-pay-head">
              <div className="bz-section-title" style={{ margin: 0 }}>Payments</div>
              {isXhinolaAdmin && (
                <button
                  type="button"
                  className="bz-secondary-btn"
                  onClick={() => setShowCarnetConfig(true)}
                >
                  <SettingsIcon size={14} />
                  Configure Cardnet
                </button>
              )}
            </div>

            {mode === 'edit' && business ? (
              <BusinessPaymentsTable businessId={business.id} />
            ) : (
              <div className="bz-list-empty">Save the business to see its payments.</div>
            )}

            {isXhinolaAdmin && (
            <Modal
              isOpen={showCarnetConfig}
              onClose={() => setShowCarnetConfig(false)}
              closeable
              animate
              autoFocus
              overrides={{ Dialog: { style: { borderRadius: '14px', maxWidth: '520px', width: '92vw' } } }}
            >
              <ModalHeader>Cardnet configuration</ModalHeader>
              <ModalBody>
                <ParagraphSmall margin="0 0 14px" color="var(--ink-500)" style={{ fontSize: 13 }}>
                  Credentials for the Cardnet payment integration. When both fields are
                  set, products in this business can use <strong>Cardnet</strong> as their
                  payment gateway; otherwise that option is disabled in the product form.
                </ParagraphSmall>

                <div className="bz-grid-2">
                  <FormControl label="Merchant ID">
                    <Input
                      value={formData.carnet_merchant_id}
                      onChange={(e) => setFormData((p) => ({
                        ...p,
                        carnet_merchant_id: (e.target as HTMLInputElement).value,
                      }))}
                      placeholder="e.g. 349089090"
                      size="compact"
                    />
                  </FormControl>
                  <FormControl label="Terminal ID">
                    <Input
                      value={formData.carnet_terminal_id}
                      onChange={(e) => setFormData((p) => ({
                        ...p,
                        carnet_terminal_id: (e.target as HTMLInputElement).value,
                      }))}
                      placeholder="e.g. 10310758"
                      size="compact"
                    />
                  </FormControl>
                </div>

                {formData.carnet_merchant_id.trim() && formData.carnet_terminal_id.trim() ? (
                  <div style={{
                    fontSize: 12,
                    color: 'var(--ok-fg)',
                    background: 'var(--ok-bg)',
                    border: '1px solid var(--ok-line)',
                    padding: '8px 10px',
                    borderRadius: 8,
                    marginTop: 8,
                  }}>
                    ✓ Cardnet is fully configured for this business.
                  </div>
                ) : (
                  <div style={{
                    fontSize: 12,
                    color: 'var(--warn-fg)',
                    background: 'var(--warn-bg-2)',
                    border: '1px solid var(--warn-line)',
                    padding: '8px 10px',
                    borderRadius: 8,
                    marginTop: 8,
                  }}>
                    Both Merchant ID and Terminal ID are required for Cardnet checkout.
                  </div>
                )}

                <ParagraphSmall margin="12px 0 0" color="var(--ink-400)" style={{ fontSize: 12 }}>
                  Changes are applied when you click <strong>Save Changes</strong>.
                </ParagraphSmall>
              </ModalBody>
              <ModalFooter>
                <ModalButton kind="tertiary" onClick={() => setShowCarnetConfig(false)}>
                  Done
                </ModalButton>
              </ModalFooter>
            </Modal>
            )}
          </div>
        )}

        {activeTab === 'team' && mode === 'edit' && business && (
          <div className="bz-tab-panel">
            <BusinessTeamList businessId={business.id} />
          </div>
        )}
        {/* Access tab temporarily disabled — keep for later:
        {activeTab === 'access' && mode === 'edit' && business && (
          <div className="bz-tab-panel">
            <BusinessAccessList businessId={business.id} />
          </div>
        )} */}

        {/* Catalog: everything bookable for this business. Products and
            services are configured the same way here (they share the same
            columns in the DB), and the mobile app renders both from it. */}
        {activeTab === 'catalog' && mode === 'edit' && business && (
          <div className="bz-tab-panel">
            <AccSection title="Services">
              <BusinessServicesList businessId={business.id} />
            </AccSection>
            <AccSection title="Products">
              <BusinessProductsList businessId={business.id} />
            </AccSection>
          </div>
        )}
      </div>
        {((isFullscreen && activeTab === 'profile') || showPagePreview) && (() => {
          const previewOpen = mode === 'edit' && business ? getBusinessOpenState(business) : null;
          // SAM-650: the app announces the next closure / special day on the profile; the saved
          // calendar (Settings › Closures & special days) is what the app reads.
          const upcoming = mode === 'edit' && business
            ? nextCalendarEvent(parseCalendar((business as { settings?: unknown }).settings))
            : null;
          const closedToday = upcoming?.kind === 'closure' && upcoming.ongoing;
          return (
            <BusinessAppPreview
              name={formData.name}
              logoKey={formData.logo_url}
              backgroundKey={formData.background_img_url}
              description={descLang === 'es' ? formData.description_es : formData.description_en}
              phone={formData.phone_number}
              websiteUrl={formData.website_url}
              instagramUrl={formData.instagram_url}
              xUrl={formData.x_url}
              facebookUrl={formData.facebook_url}
              openLabel={closedToday ? 'Closed' : previewOpen ? (previewOpen.state === 'open' ? 'Open' : 'Closed') : null}
              isOpen={!closedToday && previewOpen?.state === 'open'}
              businessId={business?.id}
              calendarNotice={upcoming ? { ...describeCalendarEvent(upcoming), tone: upcoming.kind === 'closure' ? 'closed' : 'special' } : null}
            />
          );
        })()}
      </div>

      {!isPage && (
      <footer className="bk-detail-footer">
        <button type="button" className="bz-secondary-btn" onClick={onClose} disabled={isSaving}>
          <X size={14} />
          Cancel
        </button>
        <button
          type="button"
          className="bz-primary-btn"
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
        >
          <Save size={14} />
          {isSaving ? 'Saving...' : mode === 'create' ? 'Create Business' : 'Save Changes'}
        </button>
      </footer>
      )}
    </div>
  );
};

// ─── Access list (business_access records for this business) ───────────────
// Mirrors the Events dashboard: flatten each access into person rows
// (main_guest + business_guests) showing photo, name, ID and status.
// Clicking a row opens a drawer with the same Details/Guests tabs the
// Invitations page uses.

// SAM-581: labels come from the live business_access_status records via
// useAccessStatusLabelMap (see src/hooks/useAccessTypeOptions.ts) instead of
// a hardcoded, best-effort guess that disagreed with the dashboard and
// invitations labels for the same id.
const statusClassFor = (id: any) => {
  const n = Number(id);
  if (n === 2) return 'bz-access-status confirmed';
  if (n === 1) return 'bz-access-status pending';
  if (n === 3 || n === 4) return 'bz-access-status cancelled';
  return 'bz-access-status';
};

interface PersonRow {
  accessId: string;
  accessStatusId: number;
  createDate: string;
  isMainGuest: boolean;
  name: string;
  lastName: string;
  idnum: string;
  photoUrl: string;
}

const BusinessAccessList: React.FC<{ businessId: string }> = ({ businessId }) => {
  const [search, setSearch] = useState('');
  const [selectedAccessId, setSelectedAccessId] = useState<string | null>(null);
  const [drawerTab, setDrawerTab] = useState<'details' | 'guests'>('details');
  // SAM-608: the business filter is server-side now; the client filter below stays as a guard.
  const { data, isLoading } = useBusinessAccessInvitations({ page: 1, limit: 200, business_id: businessId, entity: 'business_access' });
  const { map: statusLabelMap } = useAccessStatusLabelMap();
  const statusLabel = (id: any) => getAccessTypeLabel(statusLabelMap, id) || `Status ${id ?? '?'}`;

  const accesses = (data?.data || []).filter((it: BusinessAccess) =>
    it.entity === 'business_access' && String(it.business_id) === String(businessId),
  );

  const persons = useMemo<PersonRow[]>(() => {
    const rows: PersonRow[] = [];
    for (const acc of accesses) {
      const accessId = acc.id;
      const accessStatusId = Number((acc as any).business_access_status_id ?? 0);
      const createDate = (acc.create_date || '').toString().slice(0, 10);
      const main = acc.main_guest || ({} as any);
      rows.push({
        accessId,
        accessStatusId,
        createDate,
        isMainGuest: true,
        name: main?.name || '',
        lastName: main?.last_name || '',
        idnum: main?.idnum || '',
        photoUrl: main?.photo_url || '',
      });
      const guests = Array.isArray((acc as any).business_guests)
        ? (acc as any).business_guests
        : Array.isArray(acc.business_guest)
          ? acc.business_guest
          : [];
      for (const g of guests) {
        rows.push({
          accessId,
          accessStatusId,
          createDate,
          isMainGuest: false,
          name: g?.name || '',
          lastName: g?.last_name || '',
          idnum: g?.idnum || '',
          photoUrl: g?.photo_url || '',
        });
      }
    }
    return rows;
  }, [accesses]);

  const filteredPersons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return persons;
    return persons.filter((p) =>
      `${p.name} ${p.lastName} ${p.idnum || ''}`.toLowerCase().includes(q),
    );
  }, [persons, search]);

  const selectedAccess = useMemo(
    () => accesses.find((a) => a.id === selectedAccessId) || null,
    [accesses, selectedAccessId],
  );

  const closeDrawer = () => { setSelectedAccessId(null); setDrawerTab('details'); };

  return (
    <section className="bz-section" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div className="bz-section-title" style={{ marginBottom: 0 }}>
          Business Access · {accesses.length} ({persons.length} people)
        </div>
        <input
          className="bz-search-input"
          style={{ maxWidth: 240, paddingLeft: 12 }}
          placeholder="Search name or ID number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <TableSkeleton rows={4} bare label="Loading access records…" />
      ) : filteredPersons.length === 0 ? (
        <div className="bz-list-empty">
          {persons.length === 0 ? 'No access records for this business yet.' : 'No matches for your search.'}
        </div>
      ) : (
        <table className="bz-access-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Name</th>
              <th>ID</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredPersons.map((p, i) => (
              <tr
                key={`${p.accessId}-${p.isMainGuest ? 'main' : 'g' + i}`}
                onClick={() => setSelectedAccessId(p.accessId)}
              >
                <td>
                  <div className="bz-access-thumb">
                    {p.photoUrl ? (
                      p.photoUrl.startsWith('http') ? (
                        <img src={p.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <S3Image
                          imageKey={p.photoUrl}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          showLoading={false}
                        />
                      )
                    ) : (
                      <User size={16} color="var(--ink-400)" />
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{p.name} {p.lastName}</div>
                  {p.isMainGuest && <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>Main guest</div>}
                </td>
                <td>{p.idnum || '—'}</td>
                <td><span className={statusClassFor(p.accessStatusId)}>{statusLabel(p.accessStatusId)}</span></td>
                <td>{p.createDate || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedAccess && (
        <Drawer
          isOpen={!!selectedAccessId}
          onClose={closeDrawer}
          width="40vw"
        >
          <InvitationProvider>
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px 0 24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ink-900)', margin: 0 }}>Access details</h2>
              </div>
              <div style={{ display: 'flex', borderBottom: '1px solid var(--line)', margin: '12px 0 0 0', padding: '0 24px' }}>
                {(['details', 'guests'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setDrawerTab(t)}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontWeight: drawerTab === t ? 600 : 400,
                      color: drawerTab === t ? 'var(--ink-900)' : 'var(--ink-500)',
                      borderBottom: drawerTab === t ? '2px solid var(--tenant-primary, var(--ink-900))' : '2px solid transparent',
                      fontSize: '14px',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px 24px' }}>
                {drawerTab === 'details' ? (
                  <ConfirmationView
                    onClose={closeDrawer}
                    invitationData={selectedAccess as any}
                    isViewMode={true}
                    onAction={() => { /* read-only */ }}
                  />
                ) : (
                  <GuestsView
                    invitationData={selectedAccess as any}
                    onShowId={() => { /* lightbox lives in GuestCard */ }}
                  />
                )}
              </div>
            </div>
          </InvitationProvider>
        </Drawer>
      )}
    </section>
  );
};

// ─── Services list (for this business) ─────────────────────────────────────
// Same shape as BusinessProductsList below so both halves of the catalog are
// configured through one consistent interface. SAM-198 — service
// configuration moved to its own full page (/services/:serviceId,
// /services/new) instead of a drawer; a row click / "Add" navigates there
// (pre-scoped to this business via ?businessId=) so create/edit stays in
// sync with the main Services page and there is exactly one editor.

const BusinessServicesList: React.FC<{ businessId: string }> = ({ businessId }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: services, isLoading } = useServices(businessId);
  const list = services || [];

  // Services carry bilingual names; fall back to whichever is set.
  const serviceName = (s: Service) => s.name_es || s.name_en || s.name || 'Unnamed';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (s) =>
        serviceName(s).toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q),
    );
  }, [list, search]);

  const handleEdit = (s: Service) => {
    navigate(`/services/${s.id}?businessId=${businessId}`);
  };

  const handleAdd = () => {
    navigate(`/services/new?businessId=${businessId}`);
  };

  return (
    <section className="bz-section" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div className="bz-section-title" style={{ marginBottom: 0 }}>
          Services · {list.length}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="bz-search-input"
            style={{ maxWidth: 220, paddingLeft: 12 }}
            placeholder="Search services…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="bz-add-btn" onClick={handleAdd}>
            <Plus size={14} />
            Add
          </button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={4} bare label="Loading services…" />
      ) : filtered.length === 0 ? (
        <div className="bz-list-empty">
          {list.length === 0 ? 'No services for this business yet.' : 'No matches for your search.'}
        </div>
      ) : (
        <table className="bz-access-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Price</th>
              <th>Duration</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} onClick={() => handleEdit(s)}>
                <td style={{ width: 48 }}>
                  <div className="bz-access-thumb">
                    <SettingsIcon size={16} color="var(--ink-400)" />
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{serviceName(s)}</div>
                </td>
                <td style={{ fontWeight: 600 }}>{s.price != null ? `$${Number(s.price).toFixed(2)}` : '—'}</td>
                <td style={{ color: 'var(--ink-500)' }}>{s.duration || '—'}</td>
                <td>
                  {s.status ? (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 500,
                        backgroundColor: s.status === 'active' ? 'var(--ok-bg)' : 'var(--bad-bg)',
                        color: s.status === 'active' ? 'var(--ok-fg)' : 'var(--bad-fg)',
                      }}
                    >
                      {s.status}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--ink-400)' }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── Products list (for this business) ─────────────────────────────────────
// Reuses the existing ProductDetailDrawer so create/edit UI stays consistent
// with the main Products page.

const BusinessProductsList: React.FC<{ businessId: string }> = ({ businessId }) => {
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const { data: products, isLoading } = useProducts(businessId);
  const list = products || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.payment_gateway?.toLowerCase().includes(q),
    );
  }, [list, search]);

  const handleEdit = (p: Product) => {
    setSelectedProduct(p);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
  };

  return (
    <section className="bz-section" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div className="bz-section-title" style={{ marginBottom: 0 }}>
          Products · {list.length}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="bz-search-input"
            style={{ maxWidth: 220, paddingLeft: 12 }}
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="bz-add-btn" onClick={handleAdd}>
            <Plus size={14} />
            Add
          </button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={4} bare label="Loading products…" />
      ) : filtered.length === 0 ? (
        <div className="bz-list-empty">
          {list.length === 0 ? 'No products for this business yet.' : 'No matches for your search.'}
        </div>
      ) : (
        <table className="bz-access-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Price</th>
              <th>Gateway</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => handleEdit(p)}>
                <td style={{ width: 48 }}>
                  <div className="bz-access-thumb">
                    <Package size={16} color="var(--ink-400)" />
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{p.name || 'Unnamed'}</div>
                </td>
                <td style={{ fontWeight: 600 }}>{p.price != null ? `$${Number(p.price).toFixed(2)}` : '—'}</td>
                <td>
                  {p.payment_gateway ? (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 500,
                        backgroundColor: p.payment_gateway === 'portal' ? 'var(--ok-bg)' : 'var(--warn-bg-2)',
                        color: p.payment_gateway === 'portal' ? 'var(--ok-fg)' : 'var(--warn-fg)',
                      }}
                    >
                      {p.payment_gateway}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--ink-400)' }}>—</span>
                  )}
                </td>
                <td style={{ color: 'var(--ink-500)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.description || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ProductDetailDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        product={selectedProduct}
        mode={drawerMode}
        businessId={businessId}
        businesses={[]}
        isAdmin={false}
        hideBusinessSelector
      />
    </section>
  );
};
